import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { alpha, SxProps, Theme } from '@mui/material/styles';
import { brand, neutral } from '../../theme';

export const BETA_TOOLTIP = "PithPlay is in beta — things may change, and we'd love your feedback.";

interface BetaBadgeProps {
  /** On the dark header / auth panel rather than a light card. */
  onDark?: boolean;
  sx?: SxProps<Theme>;
}

/**
 * Small "BETA" pill shown next to the PithPlay wordmark (app header) or page
 * title (sign-in and invitation pages, which have no header). Deliberately
 * quiet: it labels the product stage, it is not a call to action.
 */
const BetaBadge: React.FC<BetaBadgeProps> = ({ onDark = false, sx }) => (
  // describeChild: the tooltip describes the badge rather than renaming it,
  // so its accessible name stays "BETA" (as a label it read "…feedback" and
  // collided with real "Feedback" fields).
  <Tooltip title={BETA_TOOLTIP} arrow describeChild>
    <Box
      component="span"
      data-testid="beta-badge"
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          flexShrink: 0,
          px: 0.75,
          py: '1px',
          borderRadius: 999,
          fontSize: '0.66rem',
          fontWeight: 800,
          lineHeight: 1.5,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          cursor: 'default',
          verticalAlign: 'middle',
          // A neutral outline: it labels the product stage, and copper is
          // kept for actions.
          color: onDark ? alpha(brand.ivory, 0.88) : brand.slate,
          backgroundColor: onDark ? alpha(brand.ivory, 0.06) : neutral[100],
          border: `1px solid ${onDark ? alpha(brand.ivory, 0.35) : brand.borderStrong}`,
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      BETA
    </Box>
  </Tooltip>
);

export default BetaBadge;
